/**
 * CSV Export — triggers a browser file download from a CSV string.
 *
 * Note: this module deliberately does NOT build CSV from client-side Kalshi
 * order/position data. Trade exports are produced server-side by the relay
 * (/api/manager/.../trade-export.csv), which already reconstructs trader side
 * via the V2 order-read path. Raw KalshiOrder.side/action/yes_price reads on
 * the client invert rebooked buy-NO orders — see reconstructOrderTraderSide in
 * src/lib/kalshiApi/orderReadV2.ts. Route any future client-side export through
 * that reconstruction.
 */

/**
 * Trigger a browser file download from a CSV string.
 *
 * For files fetched from the relay, prefer downloadCsvResponse: passing the body
 * through a string round-trip (Response.text()) UTF-8-decodes it, which silently
 * strips a server-supplied leading BOM. Use this only for CSV the browser itself
 * built as a string.
 */
export function downloadCsv(csvContent: string, filename: string): void {
  triggerDownload(new Blob([csvContent], { type: 'text/csv;charset=utf-8;' }), filename);
}

/**
 * Download a relay CSV response verbatim, preserving the exact bytes the server
 * sent (BOM and CRLF included). Reads the body as a Blob rather than decoding it
 * to a string, so the delivered file matches the relay's golden transport bytes.
 * The filename comes from Content-Disposition when present, else `fallbackName`.
 * Returns the filename actually used.
 */
export async function downloadCsvResponse(res: Response, fallbackName: string): Promise<string> {
  const blob = await res.blob();
  const filename = filenameFromContentDisposition(res.headers.get('Content-Disposition')) ?? fallbackName;
  triggerDownload(blob, filename);
  return filename;
}

/** Parse a filename from a Content-Disposition header, honoring RFC 5987 UTF-8. */
export function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  const utf8 = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8?.[1]) return decodeURIComponent(utf8[1]);
  const plain = header.match(/filename="?([^";]+)"?/i);
  return plain?.[1] ?? null;
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
